import {
  ChangeDetectionStrategy,
  Component,
  inject,
  Input,
  OnChanges,
} from '@angular/core';
import { Router } from '@angular/router';
import { FormsModule } from '@angular/forms';
import { TranslatePipe } from '@ngx-translate/core';
import {
  ISkillUsageConsultant,
  ISkillUsageProject,
} from '../../../shared/model/skill-management.model';
import {
  formatYearMonth,
  parseYearMonth,
  projectMonths,
  toYearMonth,
  totalExperienceMonths,
  YearMonth,
} from '../skill-experience';
import { compareText } from '../skill-catalog-model';
import { SkillIconComponent } from '../skill-icon/skill-icon.component';

export type UsageState = 'loading' | 'error' | 'ready';
export type UsageSort = 'duration' | 'name';

interface UsageProjectRow {
  project: ISkillUsageProject;
  months: number;
  from: string;
  to: string;
  running: boolean;
}

interface UsageGroup {
  id: string;
  name: string;
  total: number;
  projects: UsageProjectRow[];
}

@Component({
  selector: 'app-skill-usage',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [FormsModule, TranslatePipe, SkillIconComponent],
  templateUrl: './skill-usage.component.html',
  styleUrl: './skill-usage.component.scss',
})
export class SkillUsageComponent implements OnChanges {
  private readonly router = inject(Router);

  @Input() usage: ISkillUsageConsultant[] = [];
  @Input() state: UsageState = 'loading';
  @Input() today: YearMonth = toYearMonth(new Date());

  readonly presets = [0, 6, 12, 24, 36];
  minMonths = 0;
  sort: UsageSort = 'duration';
  groups: UsageGroup[] = [];
  visibleGroups: UsageGroup[] = [];
  projectCount = 0;
  private readonly collapsed = new Set<string>();

  ngOnChanges(): void {
    this.groups = this.usage.map((consultant) => this.toGroup(consultant));
    this.projectCount = this.groups.reduce(
      (sum, group) => sum + group.projects.length,
      0,
    );
    this.applyFilter();
  }

  get hasUsage(): boolean {
    return this.groups.length > 0;
  }

  setMinMonths(value: number | string | null): void {
    const months = Number(value);
    this.minMonths = Number.isInteger(months) && months >= 0 ? months : 0;
    this.applyFilter();
  }

  setSort(sort: UsageSort): void {
    this.sort = sort;
    this.applyFilter();
  }

  presetLabel(months: number): { key: string; value: number } {
    if (months === 0) {
      return { key: 'skillManagement.usage.all', value: 0 };
    }
    return months < 12
      ? { key: 'skillManagement.usage.presetMonths', value: months }
      : { key: 'skillManagement.usage.presetYears', value: months / 12 };
  }

  isOpen(group: UsageGroup): boolean {
    return !this.collapsed.has(group.id);
  }

  toggle(group: UsageGroup): void {
    if (this.collapsed.has(group.id)) {
      this.collapsed.delete(group.id);
    } else {
      this.collapsed.add(group.id);
    }
  }

  profileUrl(group: UsageGroup): string {
    return this.router.serializeUrl(
      this.router.createUrlTree(['/profile', group.id]),
    );
  }

  durationParts(months: number): { years: number; months: number } {
    return { years: Math.floor(months / 12), months: months % 12 };
  }

  private applyFilter(): void {
    this.visibleGroups = this.groups
      .filter((group) => group.total >= this.minMonths)
      .sort((a, b) =>
        this.sort === 'duration'
          ? b.total - a.total || compareText(a.name, b.name)
          : compareText(a.name, b.name),
      );
  }

  private toGroup(consultant: ISkillUsageConsultant): UsageGroup {
    const projects = [...(consultant.projekte ?? [])]
      .sort((a, b) => this.startIndex(b) - this.startIndex(a))
      .map((project) => ({
        project,
        months: projectMonths(project.beginn, project.ende, this.today),
        from: formatYearMonth(project.beginn),
        to: formatYearMonth(project.ende),
        running: !project.ende,
      }));
    return {
      id: consultant.id,
      name: [consultant.nachname, consultant.vorname]
        .filter((part) => !!part)
        .join(', '),
      total: totalExperienceMonths(consultant.projekte ?? [], this.today),
      projects,
    };
  }

  private startIndex(project: ISkillUsageProject): number {
    const start = parseYearMonth(project.beginn);
    return start ? start.year * 12 + start.month : -1;
  }
}
